'use client';

import { Info, Warning } from '@phosphor-icons/react/ssr';
import { AlertDialog as AlertDialogPrimitive } from 'radix-ui';
import { type ElementType, type ReactNode, type Ref, useRef } from 'react';

import { cn } from '../../lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Button } from './button';
import { confirmDialogStyles, modalPosition } from './overlay-styles';

export type ConfirmDialogTone = 'danger' | 'neutral';

type SectionProps = {
  /** Figma "Tone": Danger (destructive; a Danger confirm button) · Neutral (Primary). */
  tone?: ConfirmDialogTone;
  /** A question naming the object: “Delete “Q3 recap”?”. */
  title: ReactNode;
  /** Figma "Body": the consequence. */
  description?: ReactNode;
  /** Repeats the verb: “Delete reel”, not “OK”. */
  confirmLabel: ReactNode;
  cancelLabel?: ReactNode;
  /** Called on confirm; the dialog then closes. */
  onConfirm?: () => void;
  /** Replaces the Warning (Danger) / Info (Neutral) glyph. */
  icon?: ElementType<{ className?: string }>;
};

function ConfirmSections({
  tone = 'danger',
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  icon,
  confirmRef,
  inline = false,
}: SectionProps & { confirmRef?: Ref<HTMLButtonElement>; inline?: boolean }) {
  const styles = confirmDialogStyles({ tone });
  const Icon = icon ?? (tone === 'danger' ? Warning : Info);
  const cancel = <Button variant="ghost">{cancelLabel}</Button>;
  const confirm = (
    <Button
      ref={confirmRef}
      variant={tone === 'danger' ? 'danger' : 'primary'}
      onClick={onConfirm}
    >
      {confirmLabel}
    </Button>
  );
  return (
    <>
      <span data-slot="confirm-dialog-icon" aria-hidden className={styles.icon()}>
        <Icon />
      </span>
      <div data-slot="confirm-dialog-text" className={styles.text()}>
        <AlertDialogTitle data-slot="confirm-dialog-title" className={styles.title()}>
          {title}
        </AlertDialogTitle>
        {description !== undefined && (
          <AlertDialogDescription
            data-slot="confirm-dialog-description"
            className={styles.description()}
          >
            {description}
          </AlertDialogDescription>
        )}
      </div>
      <div data-slot="confirm-dialog-actions" className={styles.actions()}>
        {/* Cancel / Action need the Content's context: the static panel draws plain buttons. */}
        {inline ? cancel : <AlertDialogCancel asChild>{cancel}</AlertDialogCancel>}
        {inline ? confirm : <AlertDialogAction asChild>{confirm}</AlertDialogAction>}
      </div>
    </>
  );
}

export type ConfirmDialogProps = SectionProps & {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** The element that opens it (wrapped in a Trigger `asChild`); omit when controlled. */
  trigger?: ReactNode;
  className?: string;
};

/**
 * Short confirmation before an action (Figma: Components ↳ Modal ↳ Confirm
 * Dialog). Radix Alert Dialog: `role="alertdialog"` named by the title and
 * described by the body, focus trapped, Esc or Cancel dismiss it (an outside
 * click doesn't), focus returns to the trigger. Danger opens on Cancel (Radix
 * default, Figma "Danger: default focus on Cancel"); Neutral on the confirm
 * button.
 */
export function ConfirmDialog({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  className,
  ...sections
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const tone = sections.tone ?? 'danger';
  return (
    <AlertDialog open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {trigger !== undefined && (
        <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      )}
      <AlertDialogContent
        data-tone={tone}
        {...(sections.description === undefined ? { 'aria-describedby': undefined } : {})}
        onOpenAutoFocus={(event) => {
          if (tone === 'danger') return;
          event.preventDefault();
          confirmRef.current?.focus();
        }}
        className={confirmDialogStyles({ tone }).root({
          className: cn(modalPosition, className),
        })}
      >
        <ConfirmSections {...sections} confirmRef={confirmRef} />
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** The confirm dialog drawn in place (docs, the Figma frame): same classes, no portal. */
export function ConfirmDialogPanel({
  className,
  ...sections
}: SectionProps & { className?: string }) {
  return (
    <AlertDialogPrimitive.Root>
      <div
        data-slot="confirm-dialog-content"
        data-tone={sections.tone ?? 'danger'}
        className={confirmDialogStyles({ tone: sections.tone }).root({ className })}
      >
        <ConfirmSections {...sections} inline />
      </div>
    </AlertDialogPrimitive.Root>
  );
}
